import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { adminApi } from '../utils/api'
import clsx from 'clsx'

export function Skills() {
  const [selectedSkill, setSelectedSkill] = useState<string | null>(null)
  const [inputsJson, setInputsJson] = useState<string>('{}')
  const [testResult, setTestResult] = useState<{
    success: boolean
    result?: Record<string, unknown>
    error?: string
    execution_time_ms?: number
  } | null>(null)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['skills'],
    queryFn: adminApi.skills,
  })

  const testMutation = useMutation({
    mutationFn: ({ skill_name, inputs_json }: { skill_name: string; inputs_json: Record<string, unknown> }) =>
      adminApi.testSkill(skill_name, inputs_json),
    onSuccess: (result) => {
      setTestResult(result)
    },
    onError: (err) => {
      setTestResult({
        success: false,
        error: err instanceof Error ? err.message : 'Unknown error',
      })
    },
  })

  const selectedSkillData = data?.skills.find((s) => s.skill_name === selectedSkill)

  const handleSelectSkill = (skillName: string) => {
    setSelectedSkill(skillName)
    const skill = data?.skills.find((s) => s.skill_name === skillName)
    if (skill?.example_inputs) {
      setInputsJson(JSON.stringify(skill.example_inputs, null, 2))
    } else {
      setInputsJson('{}')
    }
    setTestResult(null)
  }

  const handleRunSkill = () => {
    if (!selectedSkill) return
    try {
      const inputs = JSON.parse(inputsJson)
      testMutation.mutate({ skill_name: selectedSkill, inputs_json: inputs })
    } catch {
      setTestResult({ success: false, error: 'Invalid JSON in inputs' })
    }
  }

  const getStatusColor = (status: string | null) => {
    switch (status) {
      case 'verified':
      case 'active':
        return 'text-signal-green'
      case 'failed':
      case 'error':
        return 'text-signal-red'
      default:
        return 'text-star-muted'
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-display font-semibold text-star mb-2">
            Skills
          </h2>
          <p className="text-star-muted text-sm">
            {data?.total ?? 0} skills available for prediction runs
          </p>
        </div>
        <button className="btn-secondary" onClick={() => refetch()}>
          ↻ Refresh
        </button>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 rounded-full border-2 border-signal-blue/30 border-t-signal-blue animate-spin"></div>
            <p className="text-star-muted font-mono text-sm">Loading skills...</p>
          </div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="glass-card p-8 text-center">
          <p className="text-signal-red font-mono mb-4">Failed to load skills</p>
          <button className="btn-primary" onClick={() => refetch()}>
            Try Again
          </button>
        </div>
      )}

      {data && data.skills.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Skill Tester */}
          <div className="lg:col-span-2 space-y-6">
            <div className="glass-card p-6">
              <h3 className="font-display font-semibold text-star mb-4">Skill Tester</h3>

              {/* Skill selector */}
              <div className="mb-4">
                <label className="data-label mb-2 block">Select Skill</label>
                <select
                  className="input w-full"
                  value={selectedSkill || ''}
                  onChange={(e) => handleSelectSkill(e.target.value)}
                >
                  <option value="">Choose a skill...</option>
                  {data.skills.map((skill) => (
                    <option key={skill.skill_name} value={skill.skill_name}>
                      {skill.skill_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Skill metadata badges */}
              {selectedSkillData && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {selectedSkillData.topic && (
                    <span className="badge-blue">
                      Topic: {selectedSkillData.topic}
                    </span>
                  )}
                  {selectedSkillData.status && (
                    <span className={clsx('badge-muted', getStatusColor(selectedSkillData.status))}>
                      Status: {selectedSkillData.status}
                    </span>
                  )}
                  {selectedSkillData.apis.length > 0 && (
                    <span className="badge-purple">
                      {selectedSkillData.apis.length} API{selectedSkillData.apis.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              )}

              {/* Inputs JSON */}
              <div className="mb-4">
                <label className="data-label mb-2 block">Inputs JSON</label>
                <textarea
                  className="input w-full font-mono text-sm min-h-[200px]"
                  value={inputsJson}
                  onChange={(e) => setInputsJson(e.target.value)}
                  placeholder='{"key": "value"}'
                  spellCheck={false}
                />
              </div>

              {/* Run button */}
              <button
                className="btn-primary w-full"
                onClick={handleRunSkill}
                disabled={!selectedSkill || testMutation.isPending}
              >
                {testMutation.isPending ? (
                  <>
                    <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></span>
                    Running...
                  </>
                ) : (
                  'Run Skill'
                )}
              </button>

              {/* Test result */}
              <AnimatePresence>
                {testResult && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="mt-6"
                  >
                    {testResult.success ? (
                      <div className="rounded-lg bg-signal-green/10 border border-signal-green/30 p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-signal-green font-semibold text-sm">Success</span>
                          {testResult.execution_time_ms && (
                            <span className="text-xs text-star-muted font-mono">
                              {testResult.execution_time_ms.toFixed(0)}ms
                            </span>
                          )}
                        </div>
                        <pre className="text-xs text-star overflow-x-auto font-mono bg-void-100 rounded p-3 mt-2">
                          {JSON.stringify(testResult.result, null, 2)}
                        </pre>
                      </div>
                    ) : (
                      <div className="rounded-lg bg-signal-red/10 border border-signal-red/30 p-4">
                        <span className="text-signal-red font-semibold text-sm">Error</span>
                        <pre className="text-xs text-signal-red/80 overflow-x-auto font-mono mt-2">
                          {testResult.error}
                        </pre>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Skill Info & List */}
          <div className="space-y-6">
            {/* Selected skill info */}
            {selectedSkillData && (
              <div className="glass-card p-6">
                <h4 className="font-display font-semibold text-star mb-3">
                  {selectedSkillData.skill_name}
                </h4>
                <p className="text-sm text-star-muted mb-4">
                  {selectedSkillData.summary}
                </p>

                {selectedSkillData.apis.length > 0 && (
                  <div className="mb-4">
                    <div className="data-label mb-2">APIs Used</div>
                    <div className="flex flex-wrap gap-2">
                      {selectedSkillData.apis.map((api, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 rounded bg-void-100 text-xs font-mono text-star-muted"
                        >
                          {api.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {selectedSkillData.last_verified_at && (
                  <div className="text-xs text-star-dim">
                    Last verified: {new Date(selectedSkillData.last_verified_at).toLocaleDateString()}
                  </div>
                )}

                <div className="mt-4">
                  <div className="data-label mb-2">Example Inputs</div>
                  <pre className="text-xs text-star-muted overflow-x-auto font-mono bg-void-100 rounded p-3">
                    {JSON.stringify(selectedSkillData.example_inputs, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {/* All skills list */}
            <div className="glass-card p-6">
              <h4 className="font-display font-semibold text-star mb-4">
                All Skills ({data.total})
              </h4>
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {data.skills.map((skill, index) => (
                  <motion.div
                    key={skill.skill_name}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.02 }}
                    className={clsx(
                      'p-3 rounded-lg border cursor-pointer transition-all',
                      selectedSkill === skill.skill_name
                        ? 'bg-signal-blue/10 border-signal-blue/30'
                        : 'bg-void-50 border-star-dim/20 hover:border-star-dim/40'
                    )}
                    onClick={() => handleSelectSkill(skill.skill_name)}
                  >
                    <div className="font-medium text-sm text-star mb-1">
                      {skill.skill_name}
                    </div>
                    <p className="text-xs text-star-muted line-clamp-2">
                      {skill.summary}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      {skill.topic && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-void-100 text-star-dim">
                          {skill.topic}
                        </span>
                      )}
                      {skill.apis.length > 0 && (
                        <span className="text-[10px] text-star-dim">
                          {skill.apis.length} API{skill.apis.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {data && data.skills.length === 0 && (
        <div className="glass-card p-12 text-center">
          <p className="text-star-muted font-mono text-lg mb-2">No skills loaded</p>
          <p className="text-star-dim text-sm">Skills will appear here when registered</p>
        </div>
      )}
    </div>
  )
}
